"use client";

import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import clsx from "clsx";
import {
  Bot,
  ChevronLeft,
  Clock3,
  Loader2,
  MessageSquare,
  SendHorizonal,
  Square,
  Trash2,
  User2,
  X,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Fragment,
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { getTextFromMessageParts } from "@/libs/assistantMessages";
import {
  getAssistantMatchContext,
  subscribeToAssistantMatchContext,
  type AssistantMatchContext,
} from "@/libs/assistantMatchContext";
import {
  shouldAnalyzeSelectedMatches,
  shouldIncludeBuildWithAiTradeContext,
  shouldIncludeDetailedTradeContext,
  type AssistantRequestMode,
} from "@/libs/assistantChatRuntime";
import {
  clearAssistantTradeContext,
  getAssistantTradeContext,
  setAssistantTradeContext,
  subscribeToAssistantTradeContext,
  type AssistantTradeContext,
} from "@/libs/assistantTradeContext";
import {
  assistantTradeStrategyRegistry,
  getAssistantTradeStrategyLabel,
  type AssistantTradeStrategy,
} from "@/libs/assistantTradeStrategies";

type AssistantPlan = "free" | "classic" | "premium";
type UsageState = {
  date: string;
  used: number;
  limit: number;
  remaining: number;
};
type AssistantConversationSummary = {
  conversationId: string;
  title: string;
  createdAt: number;
  updatedAt: number;
};
type ChatView = "home" | "conversation";
type TradeDiscoveryEventState = "pre_match" | "live";
type DiscoverableTradeStrategyKey = Extract<
  AssistantTradeStrategy,
  "scalping" | "market_making" | "sharp_money"
>;
type TradeDiscoveryWizardState = {
  eventState: TradeDiscoveryEventState | null;
  sport: string | null;
  strategy: DiscoverableTradeStrategyKey | null;
  timeWindowHours: string;
  liveWindowMinutes: string;
};
type StructuredTradeDiscoveryRequest = {
  sport: string;
  eventState: TradeDiscoveryEventState;
  strategy: DiscoverableTradeStrategyKey;
  timeWindowHours?: number;
  liveWindowMinutes?: number;
};

type HeadingTagName = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

const DISCOVERY_SPORT_OPTIONS = [
  {
    value: "Football",
    label: {
      en: "Football",
      fr: "Football",
    },
  },
  {
    value: "Basketball",
    label: {
      en: "Basketball",
      fr: "Basketball",
    },
  },
  {
    value: "Tennis",
    label: {
      en: "Tennis",
      fr: "Tennis",
    },
  },
  {
    value: "American Football",
    label: {
      en: "American Football",
      fr: "Football americain",
    },
  },
  {
    value: "Baseball",
    label: {
      en: "Baseball",
      fr: "Baseball",
    },
  },
  {
    value: "Hockey",
    label: {
      en: "Hockey",
      fr: "Hockey",
    },
  },
  {
    value: "Cricket",
    label: {
      en: "Cricket",
      fr: "Cricket",
    },
  },
  {
    value: "Esports",
    label: {
      en: "Esports",
      fr: "Esports",
    },
  },
] as const;

const DISCOVERY_TIME_WINDOW_PRESETS = [1, 2, 6, 12] as const;
const DISCOVERY_LIVE_WINDOW_PRESETS = [15, 30, 45, 60] as const;

function createTradeDiscoveryWizardState(): TradeDiscoveryWizardState {
  return {
    eventState: null,
    sport: null,
    strategy: null,
    timeWindowHours: "",
    liveWindowMinutes: "",
  };
}

function parseTradeDiscoveryTimeWindow(value: string) {
  const parsed = Number.parseInt(String(value).trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parseTradeDiscoveryLiveWindow(value: string) {
  const parsed = Number.parseInt(String(value).trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function buildTradeDiscoveryPrompt(args: {
  locale: "en" | "fr";
  eventState: TradeDiscoveryEventState;
  sport: string;
  strategyLabel: string;
  timeWindowHours?: number | null;
  liveWindowMinutes?: number | null;
}) {
  if (args.locale === "fr") {
    if (args.eventState === "live") {
      return `Utilise le trade discovery pour classer les meilleurs setups ${args.sport} live avec la strategie ${args.strategyLabel} pour les evenements en live depuis au plus ${args.liveWindowMinutes} minutes.`;
    }

    return `Utilise le trade discovery pour classer les meilleurs setups ${args.sport} pre-match avec la strategie ${args.strategyLabel} dans les ${args.timeWindowHours} prochaines heures.`;
  }

  if (args.eventState === "live") {
    return `Use trade discovery to rank the best live ${args.sport} setups with the ${args.strategyLabel} strategy for events that have been live for no more than ${args.liveWindowMinutes} minutes.`;
  }

  return `Use trade discovery to rank the best pre-match ${args.sport} setups with the ${args.strategyLabel} strategy in the next ${args.timeWindowHours} hours.`;
}

const copy = {
  en: {
    title: "OddsFantasy AI",
    subtitle: "Your personal trading assistant! 😌",
    newConversation: "New chat",
    home: "Home",
    recentConversations: "Recent conversations",
    noConversations: "No saved conversations yet.",
    you: "You",
    close: "Close assistant",
    stop: "Stop",
    usage: "Requests left today",
    discoveryUsage: "Discovery scans left today",
    tools: "Available tools",
    free: "Free",
    classic: "Classic",
    premium: "Premium",
    placeholder: "Ask me anything!",
    emptyTitle: "Ask the assistant anything",
    emptyBody:
      "Use it for platform guidance, trading concepts, calculators, or feature explanations.",
    selectedTrade: "Selected market",
    minimizeSelection: "Minimize",
    expandSelection: "Expand",
    chooseStrategy: "Choose the strategy you want to use for this setup.",
    buildWithAiPremiumOnly:
      "Build with AI is available on Classic and Premium Plans.",
    homeTitle: "Start a conversation",
    homeBody:
      "Open a saved conversation or start a new one for platform guidance, trading concepts, or event analysis.",
    thinking: "Thinking...",
    welcome:
      "I can help with OddsFantasy tools, sports trading concepts, and product navigation.",
    suggestions: ["I want to analyze and make a trade."],
    discoverTradesStarter: "Help me discover trade setups.",
    analyzeMatchesStarter: "Analyze selected matches",
    selectedMatches: "Selected matches",
    selectedMatchCount: "selected match",
    selectedMatchesCount: "selected matches",
    analyzeMatchesBody:
      "Ask about these matches or any market. I’ll fetch fresh OddsFantasy markets and research the latest sports data.",
    analyzeMatchesPlaceholder: "Ask about your selected matches...",
    exitAnalyzeMatches: "Exit",
    discoverTradesMenuTitle: "Trade discovery",
    discoverTradesChooseState: "First, choose event type.",
    discoverTradesChooseSport: "Now choose the sport to scan.",
    discoverTradesChooseStrategy: "Choose the strategy to rank by.",
    discoverTradesChooseTimePreMatch: "Set the pre-match scan window in hours.",
    discoverTradesChooseTimeLive: "Set the maximum live age in minutes.",
    discoverTradesTimePlaceholder: "Hours",
    discoverTradesLiveTimePlaceholder: "Minutes",
    discoverTradesSearch: "Scan setups",
    discoverTradesBack: "Back",
    discoverTradesCancel: "Cancel",
    discoverTradesPreMatch: "Pre-match",
    discoverTradesLive: "Live",
    reminderTitle: "OddsFantasy AI is here",
    reminderBody:
      "Ask for help with trading, platform tools, or betting concepts.",
    error:
      "An error occured while processing your request, try sending a new request",
    assistantNotConfigured:
      "The assistant is not configured. Add OPENAI_API_KEY to .env.local and restart the development server.",
    resetError: "The assistant could not start a new conversation.",
    dailyLimitReached:
      "You have reached your daily request limit for this plan.",
    tradeDiscoveryLimitReached:
      "You have reached your free trade discovery limit for today.",
    deleteConversation: "Delete conversation",
    loadingConversations: "Loading conversations...",
    openTrade: "Open trade page",
    fitScore: "Heuristic fit",
    fitDisclaimer: "Not a win probability or proven EV estimate.",
    marketOdds: "Market odds",
    suggested: "Suggested",
    starts: "Start",
    eventType: "Event type",
    strategy: "Strategy",
    matched: "Matched",
    returned: "Returned",
    competition: "Competition",
    guidance: "Guidance",
    notes: "Notes",
    netChange: "Net change",
    volatility: "Volatility",
    hours: "hours",
    minutes: "minutes",
    preMatch: "Pre-match",
    live: "Live",
    score: "Score",
    imbalance: "Imbalance",
    holders: "holders",
    hold: "hold",
    share: "share",
    trend: "Trend",
    range: "Range",
    scanSummary: "Scanned",
    buy: "Buy",
    sell: "Sell",
    wait: "Wait",
    quoteBothSides: "Quote both sides",
    event: "Event",
    scoreboard: "Scoreboard",
    market: "Market",
    outcome: "Outcome",
    position: "Position",
    buyVolume: "Buy volume",
    sellVolume: "Sell volume",
    totalVolume: "Tracked volume",
    buyShare: "Buy share",
    sellShare: "Sell share",
    buyHolders: "Buy holders",
    sellHolders: "Sell holders",
    startTime: "Start time",
  },
  fr: {
    title: "OddsFantasy AI",
    subtitle: "Votre assistant personnel de trading! 😌",
    newConversation: "Nouveau chat",
    home: "Accueil",
    recentConversations: "Conversations recentes",
    noConversations: "Aucune conversation enregistree pour le moment.",
    you: "Vous",
    close: "Fermer l'assistant",
    stop: "Arreter",
    usage: "Requetes restantes aujourd'hui",
    discoveryUsage: "Scans discovery restants aujourd'hui",
    tools: "Outils disponibles",
    free: "Free",
    classic: "Classic",
    premium: "Premium",
    placeholder: "Envie d'analyser un evenement ? ...",
    emptyTitle: "Posez n'importe quelle question a l'assistant",
    emptyBody:
      "Utilisez-le pour comprendre la plateforme, les concepts de trading, les calculateurs ou les fonctionnalites.",
    selectedTrade: "Marche selectionne",
    minimizeSelection: "Reduire",
    expandSelection: "Ouvrir",
    chooseStrategy: "Choisissez la strategie a utiliser pour ce setup.",
    buildWithAiPremiumOnly:
      "Build with AI est disponible sur Classic et Premium.",
    homeTitle: "Demarrer une conversation",
    homeBody:
      "Ouvrez une conversation enregistree ou lancez-en une nouvelle pour la plateforme, le trading ou l'analyse d'evenements.",
    thinking: "Reflexion en cours...",
    welcome:
      "Je peux vous aider avec les outils OddsFantasy, les concepts de sports trading et la navigation produit.",
    suggestions: ["Je veux analyser le marché et prendre une position."],
    discoverTradesStarter: "Aide-moi a trouver des setups de trade.",
    analyzeMatchesStarter: "Analyser les matchs selectionnes",
    selectedMatches: "Matchs selectionnes",
    selectedMatchCount: "match selectionne",
    selectedMatchesCount: "matchs selectionnes",
    analyzeMatchesBody:
      "Posez une question sur ces matchs ou un marche. Je recupererai les marches OddsFantasy et les dernieres donnees sportives.",
    analyzeMatchesPlaceholder: "Question sur les matchs selectionnes...",
    exitAnalyzeMatches: "Quitter",
    discoverTradesMenuTitle: "Trade discovery",
    discoverTradesChooseState: "D'abord, choisis le type d'evenement.",
    discoverTradesChooseSport: "Choisis maintenant le sport a scanner.",
    discoverTradesChooseStrategy: "Choisis la strategie a classer.",
    discoverTradesChooseTimePreMatch: "Definis la fenetre pre-match en heures.",
    discoverTradesChooseTimeLive:
      "Definis l'anciennete maximale du live en minutes.",
    discoverTradesTimePlaceholder: "Heures",
    discoverTradesLiveTimePlaceholder: "Minutes",
    discoverTradesSearch: "Lancer le scan",
    discoverTradesBack: "Retour",
    discoverTradesCancel: "Annuler",
    discoverTradesPreMatch: "Pre-match",
    discoverTradesLive: "Live",
    reminderTitle: "OddsFantasy AI est disponible",
    reminderBody:
      "Posez vos questions sur le trading, les outils de la plateforme ou les concepts de paris.",
    error:
      "une erreur est survenue lors du traitement de votre requete, essayez d'envoyer une nouvelle requete",
    assistantNotConfigured:
      "L'assistant n'est pas configure. Ajoutez OPENAI_API_KEY a .env.local, puis redemarrez le serveur de developpement.",
    resetError: "L'assistant n'a pas pu lancer une nouvelle conversation.",
    dailyLimitReached:
      "Vous avez atteint votre limite quotidienne de requetes pour cette offre.",
    tradeDiscoveryLimitReached:
      "Vous avez atteint votre limite gratuite de trade discovery pour aujourd'hui.",
    deleteConversation: "Supprimer la conversation",
    loadingConversations: "Chargement des conversations...",
    openTrade: "Ouvrir le trade",
    fitScore: "Fit heuristique",
    fitDisclaimer: "Ce n'est pas une probabilite de gain ni une EV prouvee.",
    marketOdds: "Cote du marche",
    suggested: "Suggestion",
    starts: "Debut",
    eventType: "Type",
    strategy: "Strategie",
    matched: "Correspondances",
    returned: "Retournes",
    competition: "Competition",
    guidance: "Points clefs",
    notes: "Notes",
    netChange: "Variation nette",
    volatility: "Volatilite",
    hours: "heures",
    minutes: "minutes",
    preMatch: "Pre-match",
    live: "Live",
    score: "Score",
    imbalance: "Desequilibre",
    holders: "detenteurs",
    hold: "detiennent",
    share: "part",
    trend: "Tendance",
    range: "Range",
    scanSummary: "Analyse",
    buy: "Acheter",
    sell: "Vendre",
    wait: "Attendre",
    quoteBothSides: "Coter les deux cotes",
    event: "Evenement",
    scoreboard: "Scoreboard",
    market: "Marche",
    outcome: "Outcome",
    position: "Position",
    buyVolume: "Volume achat",
    sellVolume: "Volume vente",
    totalVolume: "Volume suivi",
    buyShare: "Part achat",
    sellShare: "Part vente",
    buyHolders: "Acheteurs",
    sellHolders: "Vendeurs",
    startTime: "Debut",
  },
} as const;

type DiscoveryLabels = Pick<
  Record<
    | "openTrade"
    | "fitScore"
    | "fitDisclaimer"
    | "marketOdds"
    | "suggested"
    | "starts"
    | "eventType"
    | "strategy"
    | "matched"
    | "returned"
    | "competition"
    | "guidance"
    | "notes"
    | "netChange"
    | "volatility"
    | "hours"
    | "minutes"
    | "preMatch"
    | "live"
    | "score"
    | "imbalance"
    | "holders"
    | "hold"
    | "share"
    | "trend"
    | "range"
    | "scanSummary"
    | "buy"
    | "sell"
    | "wait"
    | "quoteBothSides"
    | "event"
    | "scoreboard"
    | "market"
    | "outcome"
    | "position"
    | "buyVolume"
    | "sellVolume"
    | "totalVolume"
    | "buyShare"
    | "sellShare"
    | "buyHolders"
    | "sellHolders"
    | "startTime",
    string
  >,
  | "openTrade"
  | "fitScore"
  | "fitDisclaimer"
  | "marketOdds"
  | "suggested"
  | "starts"
  | "eventType"
  | "strategy"
  | "matched"
  | "returned"
  | "competition"
  | "guidance"
  | "netChange"
  | "volatility"
  | "hours"
  | "minutes"
  | "preMatch"
  | "live"
  | "score"
  | "imbalance"
  | "holders"
  | "hold"
  | "share"
  | "trend"
  | "range"
  | "scanSummary"
  | "buy"
  | "sell"
  | "wait"
  | "quoteBothSides"
  | "event"
  | "scoreboard"
  | "market"
  | "outcome"
  | "position"
  | "buyVolume"
  | "sellVolume"
  | "totalVolume"
  | "buyShare"
  | "sellShare"
  | "buyHolders"
  | "sellHolders"
  | "startTime"
>;

type TradeDiscoveryResult = {
  status?: "ready" | "needs_input" | "not_available";
  message?: string;
  resolvedSport?: string;
  requested?: {
    eventState?: "pre_match" | "live";
    strategyLabel?: string;
    timeWindowHours?: number;
    liveWindowMinutes?: number;
  };
  scanSummary?: {
    eventsFetched?: number;
    marketsAnalyzed?: number;
    marketsSucceeded?: number;
    matchingMarkets?: number;
    eventFetchFailures?: number;
    marketAnalysisFailures?: number;
  };
  notes?: string[];
  markets?: Array<{
    rank?: number;
    fitScore?: number;
    pagePath?: string;
    event?: {
      eventId?: string;
      title?: string;
      competition?: string | null;
      gameStartTime?: string | null;
      isLive?: boolean;
      currentScore?: string | null;
    };
    selection?: {
      scoreboard?: string;
      market?: string;
      line?: string | null;
      outcome?: string;
      sellOutcome?: string;
      buyOutcome?: string | null;
      marketOdds?: number | null;
    };
    analysis?: {
      summary?: string;
      guidance?: string;
      dataQuality?: {
        historyAsOf?: number | null;
        ageMinutes?: number | null;
        coverageMinutes?: number;
        uniquePoints?: number;
        usable?: boolean;
      };
      recommendedPosition?: "buy" | "sell" | "wait";
      recommendedAction?: "quote" | "wait";
      flow?: {
        score?: number;
        threshold?: number;
        grossValueDisplay?: string;
        directionalNetValueDisplay?: string;
        directionalSharePct?: number;
        supportingWalletCount?: number;
        opposingWalletCount?: number;
      };
      trend?: {
        confidence?: string;
        strength?: string;
        absoluteChangePct?: number | null;
        recentChangePct?: number | null;
        recentDirectionAligned?: boolean;
      };
      imbalance?: {
        side?: "sell" | "buy";
        holderFractionPct?: number | null;
        holderVolumeSharePct?: number | null;
        holderCount?: number;
        holderVolumeDisplay?: string;
        sellSideVolumeDisplay?: string;
        buySideVolumeDisplay?: string;
        totalTrackedVolumeDisplay?: string;
        sellPositionCount?: number;
        buyPositionCount?: number;
      };
      range?: {
        rangeWidthPct?: number | null;
        netChangePct?: number | null;
        standardDeviationPct?: number | null;
        trendDirection?: string;
      };
    };
  }>;
};

function getDiscoverySportLabel(sport: string, locale: "en" | "fr") {
  return (
    DISCOVERY_SPORT_OPTIONS.find((option) => option.value === sport)?.label[
      locale
    ] ?? sport
  );
}

type DiscoveryRecommendation = {
  action: string;
  outcome: string | null;
};

function mergeConversationSummaries(
  current: AssistantConversationSummary[],
  incoming: AssistantConversationSummary[],
) {
  const seen = new Set<string>();
  const merged: AssistantConversationSummary[] = [];

  for (const conversation of [...current, ...incoming]) {
    if (seen.has(conversation.conversationId)) {
      continue;
    }

    seen.add(conversation.conversationId);
    merged.push(conversation);
  }

  return merged;
}

function getMessageText(message: { parts: Array<unknown> }) {
  return getTextFromMessageParts(message.parts);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function getTradeDiscoveryResultFromPart(
  part: unknown,
): TradeDiscoveryResult | null {
  if (!isRecord(part)) {
    return null;
  }

  if (part.type === "tool-discover_trade_setups") {
    const output = isRecord(part.output) ? part.output : null;
    return output ? (output as TradeDiscoveryResult) : null;
  }

  if (part.type !== "tool-invocation" || !isRecord(part.toolInvocation)) {
    return null;
  }

  const toolInvocation = part.toolInvocation;
  if (toolInvocation.toolName !== "discover_trade_setups") {
    return null;
  }

  const output = isRecord(toolInvocation.result)
    ? toolInvocation.result
    : isRecord(toolInvocation.output)
      ? toolInvocation.output
      : null;

  return output ? (output as TradeDiscoveryResult) : null;
}

function getTradeDiscoveryResults(parts: Array<unknown>) {
  return parts
    .map((part) => getTradeDiscoveryResultFromPart(part))
    .filter((result): result is TradeDiscoveryResult => Boolean(result));
}

function hasRenderableMessageContent(message: { parts: Array<unknown> }) {
  return (
    getMessageText(message).trim().length > 0 ||
    getTradeDiscoveryResults(message.parts).length > 0
  );
}

function formatDiscoveryDate(value: string | null | undefined, locale: string) {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function formatDiscoveryNumber(value: number, locale: string, maxDecimals = 2) {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: maxDecimals,
  }).format(value);
}

function formatDiscoveryPercent(
  value: number | null | undefined,
  locale: string,
  maxDecimals = 2,
) {
  if (!Number.isFinite(value)) {
    return null;
  }

  return `${formatDiscoveryNumber(Number(value), locale, maxDecimals)}%`;
}

function buildDiscoveryLink(
  locale: string,
  pagePath: string | null | undefined,
  eventId: string | null | undefined,
) {
  const normalizedPagePath =
    typeof pagePath === "string" && pagePath.startsWith("/tools/trade/")
      ? pagePath
      : null;

  if (normalizedPagePath) {
    return `/${locale}${normalizedPagePath}`;
  }

  const normalizedEventId = String(eventId ?? "").trim();
  if (!normalizedEventId) {
    return null;
  }

  return `/${locale}/tools/trade/${encodeURIComponent(normalizedEventId)}`;
}

function formatDiscoveryRecommendation(
  result: NonNullable<TradeDiscoveryResult["markets"]>[number],
  labels: DiscoveryLabels,
): DiscoveryRecommendation | null {
  const recommendedPosition = result.analysis?.recommendedPosition;
  const recommendedAction = result.analysis?.recommendedAction;

  if (recommendedPosition === "sell") {
    return {
      action: labels.sell,
      outcome:
        result.selection?.sellOutcome ?? result.selection?.outcome ?? null,
    };
  }

  if (recommendedPosition === "buy") {
    return {
      action: labels.buy,
      outcome:
        result.selection?.buyOutcome ?? result.selection?.outcome ?? null,
    };
  }

  if (recommendedPosition === "wait" || recommendedAction === "wait") {
    return {
      action: labels.wait,
      outcome: null,
    };
  }

  if (recommendedAction === "quote") {
    return {
      action: labels.quoteBothSides,
      outcome: null,
    };
  }

  return null;
}

function formatDiscoveryEventState(
  value: TradeDiscoveryResult["requested"] extends infer T
    ? T extends { eventState?: infer U }
      ? U
      : never
    : never,
  labels: DiscoveryLabels,
) {
  return value === "live"
    ? labels.live
    : value === "pre_match"
      ? labels.preMatch
      : null;
}

function formatDiscoveryWindow(
  result: TradeDiscoveryResult,
  locale: string,
  labels: DiscoveryLabels,
) {
  if (Number.isFinite(result.requested?.timeWindowHours)) {
    return `${formatDiscoveryNumber(Number(result.requested?.timeWindowHours), locale, 0)} ${labels.hours}`;
  }

  if (Number.isFinite(result.requested?.liveWindowMinutes)) {
    return `${formatDiscoveryNumber(Number(result.requested?.liveWindowMinutes), locale, 0)} ${labels.minutes}`;
  }

  return null;
}

function getDiscoverySecondaryMetric(
  result: NonNullable<TradeDiscoveryResult["markets"]>[number],
  locale: string,
  labels: DiscoveryLabels,
) {
  const flow = result.analysis?.flow;
  if (
    Number.isFinite(flow?.score) &&
    flow?.directionalNetValueDisplay &&
    Number.isFinite(flow?.supportingWalletCount)
  ) {
    return `${flow.score}/100 · ${flow.directionalNetValueDisplay} net · ${flow.supportingWalletCount} wallets`;
  }

  const imbalance = result.analysis?.imbalance;
  const holderFraction = formatDiscoveryPercent(
    imbalance?.holderFractionPct,
    locale,
  );
  const holderShare = formatDiscoveryPercent(
    imbalance?.holderVolumeSharePct,
    locale,
  );

  if (imbalance?.side && holderFraction && holderShare) {
    const sideLabel = imbalance.side === "sell" ? labels.sell : labels.buy;
    return `${sideLabel}: ${holderFraction} ${labels.holders} ${labels.hold} ${holderShare} ${labels.share}`;
  }

  if (Number.isFinite(result.analysis?.trend?.absoluteChangePct)) {
    const change = formatDiscoveryPercent(
      result.analysis?.trend?.absoluteChangePct,
      locale,
    );
    const confidence = result.analysis?.trend?.confidence;
    const strength = result.analysis?.trend?.strength;
    const suffix = [confidence, strength].filter(Boolean).join(" / ");

    return `${labels.trend}: ${change}${suffix ? ` ${suffix}` : ""}`;
  }

  if (Number.isFinite(result.analysis?.range?.rangeWidthPct)) {
    const width = formatDiscoveryPercent(
      result.analysis?.range?.rangeWidthPct,
      locale,
    );
    const direction = result.analysis?.range?.trendDirection;

    return `${labels.range}: ${width}${direction ? ` ${direction}` : ""}`;
  }

  return null;
}

function renderTradeDiscoveryResults(args: {
  results: TradeDiscoveryResult[];
  locale: string;
  labels: DiscoveryLabels;
}) {
  const { results, locale, labels } = args;

  return (
    <div className="oddsfantasy-chat-discovery-stack">
      {results.map((result, resultIndex) => {
        const markets = Array.isArray(result.markets) ? result.markets : [];
        const requestedState = formatDiscoveryEventState(
          result.requested?.eventState,
          labels,
        );
        const requestedWindow = formatDiscoveryWindow(result, locale, labels);
        const requestMeta = [
          result.resolvedSport ?? null,
          requestedState,
          result.requested?.strategyLabel ?? null,
          requestedWindow,
        ].filter(Boolean);
        const summaryLine =
          result.status === "ready" &&
          (Number.isFinite(result.scanSummary?.eventsFetched) ||
            Number.isFinite(result.scanSummary?.marketsAnalyzed) ||
            Number.isFinite(result.scanSummary?.matchingMarkets))
            ? `${labels.scanSummary}: ${result.scanSummary?.eventsFetched ?? 0} events, ${result.scanSummary?.marketsAnalyzed ?? 0} markets, ${result.scanSummary?.matchingMarkets ?? 0} ${labels.matched.toLowerCase()}`
            : null;

        return (
          <div
            key={`discovery-result-${resultIndex}`}
            className="oddsfantasy-chat-discovery-result"
          >
            {result.message ? (
              <p className="oddsfantasy-chat-discovery-summary">
                {result.message}
              </p>
            ) : null}

            {requestMeta.length > 0 ? (
              <div className="oddsfantasy-chat-discovery-request-meta">
                {requestMeta.map((value) => (
                  <span
                    key={`discovery-request-meta-${resultIndex}-${value}`}
                    className="oddsfantasy-chat-discovery-chip"
                  >
                    {value}
                  </span>
                ))}
              </div>
            ) : null}

            {summaryLine ? (
              <p className="oddsfantasy-chat-discovery-meta">{summaryLine}</p>
            ) : null}
            {Array.isArray(result.notes) && result.notes.length > 0 ? (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-400">
                {result.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}

            {markets.length > 0 ? (
              <div className="oddsfantasy-chat-discovery-grid">
                {markets.map((market, marketIndex) => {
                  const href = buildDiscoveryLink(
                    locale,
                    market.pagePath,
                    market.event?.eventId,
                  );
                  const marketName = [
                    market.selection?.market,
                    market.selection?.line,
                  ]
                    .filter(Boolean)
                    .join(" ");
                  const suggestion = formatDiscoveryRecommendation(
                    market,
                    labels,
                  );
                  const metric = getDiscoverySecondaryMetric(
                    market,
                    locale,
                    labels,
                  );
                  const startTime = formatDiscoveryDate(
                    market.event?.gameStartTime,
                    locale,
                  );
                  const subtitleParts = [
                    market.event?.competition ?? null,
                    market.event?.isLive ? labels.live : startTime,
                    market.event?.currentScore ?? null,
                  ].filter(Boolean);
                  const detailRows = [
                    {
                      label: labels.competition,
                      value: market.event?.competition ?? null,
                    },
                    {
                      label: labels.scoreboard,
                      value: market.selection?.scoreboard ?? null,
                    },
                    {
                      label: labels.market,
                      value: marketName || null,
                    },
                    {
                      label: labels.outcome,
                      value: market.selection?.outcome ?? null,
                    },
                    {
                      label: labels.position,
                      value: suggestion?.action ?? null,
                    },
                    {
                      label: labels.marketOdds,
                      value: Number.isFinite(market.selection?.marketOdds)
                        ? formatDiscoveryNumber(
                            Number(market.selection?.marketOdds),
                            locale,
                          )
                        : null,
                    },
                    {
                      label: labels.startTime,
                      value: market.event?.isLive ? labels.live : startTime,
                    },
                    {
                      label: labels.score,
                      value: market.event?.currentScore ?? null,
                    },
                    {
                      label: labels.sellVolume,
                      value:
                        market.analysis?.imbalance?.sellSideVolumeDisplay ??
                        null,
                    },
                    {
                      label: labels.buyVolume,
                      value:
                        market.analysis?.imbalance?.buySideVolumeDisplay ??
                        null,
                    },
                    {
                      label: labels.sellHolders,
                      value: Number.isFinite(
                        market.analysis?.imbalance?.sellPositionCount,
                      )
                        ? formatDiscoveryNumber(
                            Number(
                              market.analysis?.imbalance?.sellPositionCount,
                            ),
                            locale,
                            0,
                          )
                        : null,
                    },
                    {
                      label: labels.buyHolders,
                      value: Number.isFinite(
                        market.analysis?.imbalance?.buyPositionCount,
                      )
                        ? formatDiscoveryNumber(
                            Number(
                              market.analysis?.imbalance?.buyPositionCount,
                            ),
                            locale,
                            0,
                          )
                        : null,
                    },
                    {
                      label: labels.totalVolume,
                      value:
                        market.analysis?.imbalance?.totalTrackedVolumeDisplay ??
                        null,
                    },
                    {
                      label: labels.imbalance,
                      value: metric,
                    },
                    {
                      label: labels.netChange,
                      value: formatDiscoveryPercent(
                        market.analysis?.range?.netChangePct,
                        locale,
                      ),
                    },
                    {
                      label: labels.volatility,
                      value: formatDiscoveryPercent(
                        market.analysis?.range?.standardDeviationPct,
                        locale,
                      ),
                    },
                  ].filter(
                    (
                      row,
                    ): row is {
                      label: string;
                      value: string;
                    } =>
                      Boolean(row.value && String(row.value).trim().length > 0),
                  );

                  return (
                    <article
                      key={`discovery-market-${resultIndex}-${market.event?.eventId ?? marketIndex}-${market.rank ?? marketIndex}`}
                      className="oddsfantasy-chat-discovery-card"
                    >
                      <div className="oddsfantasy-chat-discovery-card-top">
                        <span className="oddsfantasy-chat-discovery-rank">
                          #{market.rank ?? marketIndex + 1}
                        </span>
                        {Number.isFinite(market.fitScore) ? (
                          <span className="oddsfantasy-chat-discovery-fit">
                            {labels.fitScore}:{" "}
                            {formatDiscoveryNumber(
                              Number(market.fitScore),
                              locale,
                            )}
                          </span>
                        ) : null}
                      </div>

                      <p className="oddsfantasy-chat-discovery-title">
                        {market.event?.title ??
                          market.event?.competition ??
                          "Untitled event"}
                      </p>

                      {subtitleParts.length > 0 ? (
                        <p className="oddsfantasy-chat-discovery-subtitle">
                          {subtitleParts.join(" | ")}
                        </p>
                      ) : null}

                      <p className="mt-2 text-xs text-amber-200">
                        {labels.fitDisclaimer}
                      </p>

                      {market.analysis?.summary ? (
                        <p className="mt-2 text-sm leading-5 text-slate-300">
                          {market.analysis.summary}
                        </p>
                      ) : null}

                      {market.analysis?.guidance ? (
                        <p className="mt-2 text-xs leading-5 text-slate-400">
                          {market.analysis.guidance}
                        </p>
                      ) : null}

                      <div className="oddsfantasy-chat-discovery-table">
                        {detailRows.map((row) => (
                          <div
                            key={`${market.event?.eventId ?? marketIndex}-${row.label}`}
                            className="oddsfantasy-chat-discovery-row"
                          >
                            <span className="oddsfantasy-chat-discovery-row-label">
                              {row.label}
                            </span>
                            <span className="oddsfantasy-chat-discovery-row-value">
                              {row.label === labels.position && suggestion ? (
                                <span className="oddsfantasy-chat-discovery-position">
                                  <span className="oddsfantasy-chat-discovery-position-action">
                                    {suggestion.action}
                                  </span>
                                  {suggestion.outcome ? (
                                    <>
                                      <span
                                        aria-hidden="true"
                                        className="oddsfantasy-chat-discovery-position-divider"
                                      />
                                      <span className="oddsfantasy-chat-discovery-position-outcome">
                                        {suggestion.outcome}
                                      </span>
                                    </>
                                  ) : null}
                                </span>
                              ) : (
                                row.value
                              )}
                            </span>
                          </div>
                        ))}
                      </div>

                      {href ? (
                        <Link
                          href={href}
                          scroll={false}
                          className="oddsfantasy-chat-discovery-link"
                        >
                          {labels.openTrade}
                        </Link>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function renderInlineMarkdown(content: string) {
  const nodes: ReactNode[] = [];
  const pattern =
    /(\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_)/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null = pattern.exec(content);

  while (match) {
    if (match.index > lastIndex) {
      nodes.push(content.slice(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      nodes.push(
        <a
          key={`link-${match.index}`}
          href={match[3]}
          target="_blank"
          rel="noreferrer"
          className="oddsfantasy-chat-markdown-link"
        >
          {match[2]}
        </a>,
      );
    } else if (match[4]) {
      nodes.push(
        <code
          key={`code-${match.index}`}
          className="oddsfantasy-chat-markdown-inline-code"
        >
          {match[4]}
        </code>,
      );
    } else if (match[5] || match[6]) {
      nodes.push(
        <strong key={`strong-${match.index}`}>{match[5] || match[6]}</strong>,
      );
    } else if (match[7] || match[8]) {
      nodes.push(<em key={`em-${match.index}`}>{match[7] || match[8]}</em>);
    }

    lastIndex = match.index + match[0].length;
    match = pattern.exec(content);
  }

  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : content;
}

function renderAssistantMarkdown(content: string) {
  const lines = content.split("\n");
  const blocks: ReactNode[] = [];
  let paragraphLines: string[] = [];
  let listItems: Array<{ text: string; ordered: boolean }> = [];
  let codeFenceLines: string[] = [];
  let codeFenceLanguage = "";
  let inCodeFence = false;

  function flushParagraph() {
    if (paragraphLines.length === 0) {
      return;
    }

    const paragraph = paragraphLines.join(" ").trim();

    if (paragraph) {
      blocks.push(
        <p
          key={`paragraph-${blocks.length}`}
          className="oddsfantasy-chat-markdown-paragraph"
        >
          {renderInlineMarkdown(paragraph)}
        </p>,
      );
    }

    paragraphLines = [];
  }

  function flushList() {
    if (listItems.length === 0) {
      return;
    }

    const ordered = listItems[0]?.ordered;
    const ListTag = ordered ? "ol" : "ul";

    blocks.push(
      <ListTag
        key={`list-${blocks.length}`}
        className={clsx(
          "oddsfantasy-chat-markdown-list",
          ordered && "oddsfantasy-chat-markdown-list-ordered",
        )}
      >
        {listItems.map((item, index) => (
          <li key={`item-${index}`}>{renderInlineMarkdown(item.text)}</li>
        ))}
      </ListTag>,
    );

    listItems = [];
  }

  function flushCodeFence() {
    if (codeFenceLines.length === 0 && !codeFenceLanguage) {
      return;
    }

    blocks.push(
      <pre
        key={`codeblock-${blocks.length}`}
        className="oddsfantasy-chat-markdown-code-block"
      >
        <code>{codeFenceLines.join("\n")}</code>
      </pre>,
    );

    codeFenceLines = [];
    codeFenceLanguage = "";
  }

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (line.startsWith("```")) {
      flushParagraph();
      flushList();

      if (inCodeFence) {
        inCodeFence = false;
        flushCodeFence();
      } else {
        inCodeFence = true;
        codeFenceLanguage = line.slice(3).trim();
      }

      continue;
    }

    if (inCodeFence) {
      codeFenceLines.push(rawLine);
      continue;
    }

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);

    if (headingMatch) {
      flushParagraph();
      flushList();
      const level = Math.min(headingMatch[1].length, 6);
      const headingTagByLevel: Record<number, HeadingTagName> = {
        1: "h1",
        2: "h2",
        3: "h3",
        4: "h4",
        5: "h5",
        6: "h6",
      };
      const HeadingTag = headingTagByLevel[level] || "h3";

      blocks.push(
        <HeadingTag
          key={`heading-${blocks.length}`}
          className={clsx(
            "oddsfantasy-chat-markdown-heading",
            `oddsfantasy-chat-markdown-heading-${level}`,
          )}
        >
          {renderInlineMarkdown(headingMatch[2].trim())}
        </HeadingTag>,
      );
      continue;
    }

    const unorderedListMatch = line.match(/^[-*]\s+(.*)$/);

    if (unorderedListMatch) {
      flushParagraph();
      listItems.push({ text: unorderedListMatch[1].trim(), ordered: false });
      continue;
    }

    const orderedListMatch = line.match(/^\d+\.\s+(.*)$/);

    if (orderedListMatch) {
      flushParagraph();
      listItems.push({ text: orderedListMatch[1].trim(), ordered: true });
      continue;
    }

    const blockquoteMatch = line.match(/^>\s?(.*)$/);

    if (blockquoteMatch) {
      flushParagraph();
      flushList();
      blocks.push(
        <blockquote
          key={`blockquote-${blocks.length}`}
          className="oddsfantasy-chat-markdown-blockquote"
        >
          {renderInlineMarkdown(blockquoteMatch[1].trim())}
        </blockquote>,
      );
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  flushCodeFence();

  return (
    <div className="oddsfantasy-chat-markdown">
      {blocks.map((block, index) => (
        <Fragment key={index}>{block}</Fragment>
      ))}
    </div>
  );
}

function getChatErrorMessage(
  error: Error | undefined,
  fallback: string,
  assistantNotConfigured: string,
) {
  if (!error?.message) {
    return fallback;
  }

  try {
    const payload = JSON.parse(error.message) as {
      code?: unknown;
    };

    if (payload?.code === "assistant_not_configured") {
      return assistantNotConfigured;
    }
  } catch {
    // The transport can also return a plain error string.
  }

  return error.message.includes("OPENAI_API_KEY")
    ? assistantNotConfigured
    : fallback;
}

export default function OddsFantasyAssistant({ lang }: { lang: string }) {
  const locale = lang === "fr" ? "fr" : "en";
  const text = copy[locale];
  const pathname = usePathname();
  const isHomePage = pathname === `/${locale}` || pathname === "/";
  const isTradePage = pathname.split("/").filter(Boolean).includes("trade");
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [plan, setPlan] = useState<AssistantPlan>("free");
  const [usage, setUsage] = useState<UsageState | null>(null);
  const [tradeDiscoveryUsage, setTradeDiscoveryUsage] =
    useState<UsageState | null>(null);
  const [enabledTools, setEnabledTools] = useState<
    Array<{ key: string; label: string }>
  >([]);
  const [availableTradeDiscoverySports, setAvailableTradeDiscoverySports] =
    useState<string[]>([]);
  const [conversations, setConversations] = useState<
    AssistantConversationSummary[]
  >([]);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [nextConversationOffset, setNextConversationOffset] = useState<
    number | null
  >(null);
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(null);
  const [currentView, setCurrentView] = useState<ChatView>("home");
  const [selectedTradeContext, setSelectedTradeContext] =
    useState<AssistantTradeContext | null>(null);
  const [selectedMatchContext, setSelectedMatchContext] =
    useState<AssistantMatchContext | null>(null);
  const [isAnalyzeMatchesMode, setIsAnalyzeMatchesMode] = useState(false);
  const [isSelectedTradeCardMinimized, setIsSelectedTradeCardMinimized] =
    useState(false);
  const [tradeStrategyNotice, setTradeStrategyNotice] = useState("");
  const [metaError, setMetaError] = useState("");
  const [showReminder, setShowReminder] = useState(false);
  const [isSelectingConversation, setIsSelectingConversation] = useState(false);
  const [isLoadingMoreConversations, setIsLoadingMoreConversations] =
    useState(false);
  const [deletingConversationId, setDeletingConversationId] = useState<
    string | null
  >(null);
  const [tradeDiscoveryWizard, setTradeDiscoveryWizard] =
    useState<TradeDiscoveryWizardState | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const savedBodyScrollTopRef = useRef(0);
  const shouldRestoreBodyScrollRef = useRef(false);
  const lastSentPagePathRef = useRef(pathname);
  const hasHydratedConversationRef = useRef(false);
  const hasLocalChatActivityRef = useRef(false);
  const [pageContextChanged, setPageContextChanged] = useState(false);

  const { messages, sendMessage, setMessages, status, stop, error } = useChat({
    transport: new DefaultChatTransport({
      api: "/api/chat",
      body: {
        lang: locale,
        pagePath: pathname,
      },
    }),
  });

  const isSubmitting = status === "submitted";
  const isStreaming = status === "streaming";
  const isBusy = isSubmitting || isStreaming;
  const lastRenderableMessage = [...messages]
    .reverse()
    .find((message) => hasRenderableMessageContent(message));
  const showAssistantTypingRow =
    isBusy && lastRenderableMessage?.role !== "assistant";
  const currentPageEventId =
    pathname.match(/\/tools\/trade\/([^/?#]+)/i)?.[1] ?? null;
  const currentPageEventIds = currentPageEventId
    ? currentPageEventId
        .split(",")
        .map((eventId) => eventId.trim())
        .filter(Boolean)
    : [];

  function eventIdsOverlap(
    left: string | null | undefined,
    rightIds: string[],
  ) {
    const leftIds = String(left ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    if (leftIds.length === 0 || rightIds.length === 0) {
      return false;
    }

    return leftIds.some((value) => rightIds.includes(value));
  }

  const activeTradeContext =
    selectedTradeContext &&
    currentPageEventIds.length > 0 &&
    eventIdsOverlap(selectedTradeContext.eventId, currentPageEventIds)
      ? selectedTradeContext
      : null;
  const canUseTradeAnalysis =
    isTradePage && enabledTools.some((tool) => tool.key === "trade_analysis");
  const canUseTradeDiscovery = enabledTools.some(
    (tool) => tool.key === "trade_discovery",
  );
  const canUseAnalyzeMatches = enabledTools.some(
    (tool) => tool.key === "analyze_matches",
  );
  const visibleTradeStrategies = assistantTradeStrategyRegistry;
  const tradeDiscoveryStrategies = visibleTradeStrategies.filter(
    (strategy) =>
      strategy.key === "scalping" ||
      strategy.key === "market_making" ||
      strategy.key === "sharp_money",
  ) as Array<
    (typeof assistantTradeStrategyRegistry)[number] & {
      key: DiscoverableTradeStrategyKey;
    }
  >;
  const canAnalyzeSelectedTrade =
    Boolean(activeTradeContext) && canUseTradeAnalysis;
  const canAnalyzeSelectedMatches =
    canUseAnalyzeMatches && Boolean(selectedMatchContext?.matches.length);
  const starterSuggestions = [
    ...text.suggestions,
    ...(canUseTradeDiscovery ? [text.discoverTradesStarter] : []),
    ...(canAnalyzeSelectedMatches ? [text.analyzeMatchesStarter] : []),
  ];

  const loadAssistantMeta = useCallback(
    async (options?: {
      hydrateMessages?: boolean;
      conversationOffset?: number;
      conversationLimit?: number;
      includeMessages?: boolean;
      appendConversations?: boolean;
    }) => {
      const searchParams = new URLSearchParams();

      if (Number.isFinite(options?.conversationOffset)) {
        searchParams.set(
          "conversationOffset",
          String(Math.max(0, Math.floor(Number(options?.conversationOffset)))),
        );
      }

      if (Number.isFinite(options?.conversationLimit)) {
        searchParams.set(
          "conversationLimit",
          String(Math.max(1, Math.floor(Number(options?.conversationLimit)))),
        );
      }

      if (options?.includeMessages === false) {
        searchParams.set("includeMessages", "false");
      }

      const response = await fetch(
        searchParams.size > 0
          ? `/api/chat?${searchParams.toString()}`
          : "/api/chat",
        { method: "GET" },
      );

      if (!response.ok) {
        throw new Error("Failed to load assistant metadata.");
      }

      const payload = await response.json();

      setPlan(payload.plan || "free");
      setUsage(payload.usage || null);
      setTradeDiscoveryUsage(payload.tradeDiscoveryUsage || null);
      setEnabledTools(
        Array.isArray(payload.enabledTools) ? payload.enabledTools : [],
      );
      setAvailableTradeDiscoverySports(
        Array.isArray(payload.availableTradeDiscoverySports)
          ? payload.availableTradeDiscoverySports.filter(
              (sport): sport is string =>
                typeof sport === "string" && sport.trim().length > 0,
            )
          : [],
      );
      setConversations((current) =>
        options?.appendConversations
          ? mergeConversationSummaries(
              current,
              Array.isArray(payload.conversations) ? payload.conversations : [],
            )
          : Array.isArray(payload.conversations)
            ? payload.conversations
            : [],
      );
      setHasMoreConversations(payload.hasMoreConversations === true);
      setNextConversationOffset(
        Number.isFinite(payload.nextConversationOffset)
          ? Number(payload.nextConversationOffset)
          : null,
      );
      setCurrentConversationId(payload.activeConversationId || null);

      if (
        options?.includeMessages !== false &&
        options?.hydrateMessages !== false &&
        !hasHydratedConversationRef.current &&
        !hasLocalChatActivityRef.current
      ) {
        setMessages(Array.isArray(payload.messages) ? payload.messages : []);
        hasHydratedConversationRef.current = true;
      }

      setMetaError("");
    },
    [setMessages],
  );

  function formatConversationTimestamp(timestamp: number) {
    if (!Number.isFinite(timestamp)) {
      return "";
    }

    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(timestamp));
  }

  function formatTradeSelectionTitle(context: AssistantTradeContext) {
    const marketLabel = [context.market, context.line]
      .filter(Boolean)
      .join(" ");
    return `${marketLabel}: ${context.outcome}`.trim();
  }

  function getStrategySubmitPrompt(strategy: AssistantTradeStrategy) {
    if (strategy === "build_with_ai") {
      if (plan === "free") {
        return locale === "fr"
          ? "Build with AI est disponible uniquement sur Classic et Premium."
          : "Build with AI is available on Classic and Premium Plans.";
      }

      return locale === "fr"
        ? "Utilise le contexte complet du trade modal et la recherche web si necessaire pour construire avec moi une strategie sur ce marche et conclure s'il faut Buy, Sell ou Wait."
        : "Use the full trade modal context and web research when needed to help me build a strategy for this market and conclude whether the position should be Buy, Sell, or Wait.";
    }

    const label = getAssistantTradeStrategyLabel(strategy);
    return locale === "fr"
      ? `Analyse ce marche selectionne avec la strategie ${label}.`
      : `Analyze this selected market using the ${label} strategy.`;
  }

  function getTradeContextForStrategy(
    strategy: AssistantTradeStrategy,
    context: AssistantTradeContext | null,
  ) {
    if (!context) {
      return null;
    }

    return {
      ...context,
      assistantMode: strategy === "build_with_ai" ? "build_with_ai" : null,
      updatedAt: Date.now(),
    } satisfies AssistantTradeContext;
  }

  function buildChatRequestTradeContext(
    context: AssistantTradeContext | null | undefined,
    options?: {
      includeTradeAnalysisContext?: boolean;
      includeBuildWithAiContext?: boolean;
    },
  ) {
    if (!context) {
      return null;
    }

    const baseContext: AssistantTradeContext = {
      eventId: context.eventId,
      eventTitle: context.eventTitle,
      gameStartTime: context.gameStartTime ?? null,
      currentScore: context.currentScore ?? null,
      scoreboard: context.scoreboard,
      market: context.market,
      line: context.line ?? null,
      outcome: context.outcome,
      outcomeIndex: context.outcomeIndex,
      marketId: context.marketId,
      conditionId: context.conditionId,
      sellTokenId: context.sellTokenId,
      buyTokenId: context.buyTokenId ?? null,
      assistantMode: context.assistantMode ?? null,
      isLive: context.isLive ?? null,
      updatedAt: context.updatedAt,
    };

    if (options?.includeTradeAnalysisContext !== true) {
      return baseContext;
    }

    const detailedContext: AssistantTradeContext = {
      ...baseContext,
      marketDescription: context.marketDescription ?? null,
      sellOutcome: context.sellOutcome ?? context.outcome,
      buyOutcome: context.buyOutcome ?? null,
      marketOdds: context.marketOdds ?? null,
      orderBookSnapshot: context.orderBookSnapshot ?? null,
      history: Array.isArray(context.history) ? context.history : [],
    };

    if (options?.includeBuildWithAiContext !== true) {
      return detailedContext;
    }

    return {
      ...detailedContext,
    } satisfies AssistantTradeContext;
  }

  useEffect(() => {
    let active = true;

    async function loadInitialAssistantMeta() {
      try {
        if (!active) {
          return;
        }

        await loadAssistantMeta({
          conversationOffset: 0,
          conversationLimit: 10,
        });
      } catch (error) {
        if (!active) {
          return;
        }

        setMetaError(text.error);
      }
    }

    void loadInitialAssistantMeta();

    return () => {
      active = false;
    };
  }, [loadAssistantMeta, text.error]);

  useEffect(() => {
    setSelectedTradeContext(isTradePage ? getAssistantTradeContext() : null);

    return subscribeToAssistantTradeContext((context) => {
      setSelectedTradeContext(isTradePage ? context : null);
    });
  }, [isTradePage]);

  useEffect(() => {
    if (isTradePage) {
      return;
    }

    clearAssistantTradeContext();
    setSelectedTradeContext(null);
    setIsSelectedTradeCardMinimized(false);
  }, [isTradePage]);

  useEffect(() => {
    setSelectedMatchContext(getAssistantMatchContext());

    return subscribeToAssistantMatchContext((context) => {
      setSelectedMatchContext(context);
      if (!context) {
        setIsAnalyzeMatchesMode(false);
      }
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!shouldRestoreBodyScrollRef.current) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      if (bodyRef.current) {
        bodyRef.current.scrollTop = savedBodyScrollTopRef.current;
      }
      shouldRestoreBodyScrollRef.current = false;
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    bodyRef.current?.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, status]);

  useEffect(() => {
    if (isOpen || !isHomePage) {
      setShowReminder(false);
      return;
    }

    setShowReminder(true);
    const timeoutId = window.setTimeout(() => {
      setShowReminder(false);
    }, 6000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isHomePage, isOpen, pathname]);

  useEffect(() => {
    if (pathname === lastSentPagePathRef.current) {
      return;
    }

    setPageContextChanged(true);
  }, [pathname]);

  const loadMoreConversations = useCallback(async () => {
    if (
      isLoadingMoreConversations ||
      isSelectingConversation ||
      !hasMoreConversations
    ) {
      return;
    }

    setIsLoadingMoreConversations(true);

    try {
      await loadAssistantMeta({
        hydrateMessages: false,
        includeMessages: false,
        appendConversations: true,
        conversationOffset: nextConversationOffset ?? conversations.length,
        conversationLimit: 10,
      });
    } finally {
      setIsLoadingMoreConversations(false);
    }
  }, [
    conversations.length,
    hasMoreConversations,
    isLoadingMoreConversations,
    isSelectingConversation,
    loadAssistantMeta,
    nextConversationOffset,
  ]);

  const handleBodyScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      savedBodyScrollTopRef.current = event.currentTarget.scrollTop;

      if (
        currentView !== "home" ||
        isLoadingMoreConversations ||
        isSelectingConversation ||
        !hasMoreConversations
      ) {
        return;
      }

      const target = event.currentTarget;

      if (target.scrollTop + target.clientHeight < target.scrollHeight - 120) {
        return;
      }

      void loadMoreConversations();
    },
    [
      currentView,
      hasMoreConversations,
      isLoadingMoreConversations,
      isSelectingConversation,
      loadMoreConversations,
    ],
  );

  const openAssistant = useCallback(() => {
    setShowReminder(false);
    setIsOpen(true);
  }, []);

  const closeAssistant = useCallback(() => {
    if (bodyRef.current) {
      savedBodyScrollTopRef.current = bodyRef.current.scrollTop;
    }
    shouldRestoreBodyScrollRef.current = true;
    setIsOpen(false);
  }, []);

  useEffect(() => {
    window.addEventListener("oddsfantasy:open-assistant", openAssistant);
    return () => {
      window.removeEventListener("oddsfantasy:open-assistant", openAssistant);
    };
  }, [openAssistant]);

  async function submitMessage(
    textToSend: string,
    options?: {
      requestMode?: AssistantRequestMode;
      selectedTradeContextOverride?: AssistantTradeContext | null;
      tradeDiscoveryRequest?: StructuredTradeDiscoveryRequest | null;
    },
  ) {
    const value = textToSend.trim();

    if (
      !value ||
      isBusy ||
      isSelectingConversation ||
      (usage && usage.remaining <= 0)
    ) {
      return;
    }

    try {
      const targetConversationId = currentConversationId;
      const requestMode =
        options?.requestMode ??
        (isAnalyzeMatchesMode ||
        shouldAnalyzeSelectedMatches(
          value,
          Boolean(selectedMatchContext?.matches.length),
        )
          ? "analyze_matches"
          : "general");
      const targetTradeContext =
        options?.selectedTradeContextOverride ?? activeTradeContext;

      setInput("");
      setTradeDiscoveryWizard(null);
      setIsAnalyzeMatchesMode(requestMode === "analyze_matches");
      openAssistant();
      setCurrentView("conversation");
      hasLocalChatActivityRef.current = true;

      await sendMessage(
        { text: value },
        {
          body: {
            conversationId: targetConversationId,
            forceNewConversation: !targetConversationId,
            requestMode,
            lang: locale,
            pagePath: pathname,
            pageContextChanged,
            selectedTradeContext: buildChatRequestTradeContext(
              targetTradeContext,
              {
                includeTradeAnalysisContext:
                  shouldIncludeDetailedTradeContext(requestMode),
                includeBuildWithAiContext:
                  shouldIncludeBuildWithAiTradeContext(requestMode),
              },
            ),
            includeBuildWithAiContext:
              shouldIncludeBuildWithAiTradeContext(requestMode),
            selectedMatchContext:
              canUseAnalyzeMatches ? selectedMatchContext : null,
            tradeDiscoveryRequest: options?.tradeDiscoveryRequest ?? null,
          },
        },
      );

      lastSentPagePathRef.current = pathname;
      setPageContextChanged(false);

      if (usage) {
        setUsage({
          ...usage,
          used: usage.used + 1,
          remaining: Math.max(usage.remaining - 1, 0),
        });
      }

      await loadAssistantMeta({ hydrateMessages: false });
    } catch (error) {
      console.error("Failed to send assistant message:", error);
    }
  }

  async function startNewConversation() {
    if (isBusy || isSelectingConversation) {
      return;
    }

    try {
      setCurrentConversationId(null);
      setMessages([]);
      setInput("");
      setTradeDiscoveryWizard(null);
      setIsAnalyzeMatchesMode(false);
      setMetaError("");
      setCurrentView("conversation");
      hasHydratedConversationRef.current = true;
      hasLocalChatActivityRef.current = false;
      setPageContextChanged(false);
    } catch (error) {
      console.error("Failed to start a new assistant conversation:", error);
      setMetaError(text.resetError);
    }
  }

  async function openConversation(conversationId: string) {
    if (isBusy || isSelectingConversation) {
      return;
    }

    setIsSelectingConversation(true);

    try {
      const response = await fetch("/api/chat", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "select",
          conversationId,
          includeMessages: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to select assistant conversation.");
      }

      const payload = await response.json();

      setCurrentConversationId(payload.activeConversationId || null);
      setMessages(Array.isArray(payload.messages) ? payload.messages : []);
      setTradeDiscoveryWizard(null);
      setIsAnalyzeMatchesMode(false);
      setCurrentView("conversation");
      setMetaError("");
      hasHydratedConversationRef.current = true;
      hasLocalChatActivityRef.current = false;
    } catch (error) {
      console.error("Failed to open assistant conversation:", error);
      setMetaError(text.error);
    } finally {
      setIsSelectingConversation(false);
    }
  }

  async function deleteConversation(conversationId: string) {
    if (
      isBusy ||
      isSelectingConversation ||
      deletingConversationId === conversationId
    ) {
      return;
    }

    setDeletingConversationId(conversationId);

    try {
      const response = await fetch("/api/chat", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "delete",
          conversationId,
          conversationOffset: 0,
          conversationLimit: Math.max(conversations.length, 10),
          includeMessages: false,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete assistant conversation.");
      }

      const payload = await response.json();

      setConversations(
        Array.isArray(payload.conversations) ? payload.conversations : [],
      );
      setHasMoreConversations(payload.hasMoreConversations === true);
      setNextConversationOffset(
        Number.isFinite(payload.nextConversationOffset)
          ? Number(payload.nextConversationOffset)
          : null,
      );
      setCurrentConversationId(payload.activeConversationId || null);

      if (currentConversationId === conversationId) {
        setMessages([]);
        setCurrentView("home");
        setIsAnalyzeMatchesMode(false);
        hasHydratedConversationRef.current = true;
        hasLocalChatActivityRef.current = false;
      }

      setMetaError("");
    } catch (error) {
      console.error("Failed to delete assistant conversation:", error);
      setMetaError(text.error);
    } finally {
      setDeletingConversationId(null);
    }
  }

  function getPlanLabel(value: AssistantPlan) {
    return text[value];
  }

  function startTradeDiscoveryWizard() {
    if (
      plan === "free" &&
      tradeDiscoveryUsage &&
      tradeDiscoveryUsage.remaining <= 0
    ) {
      setMetaError(text.tradeDiscoveryLimitReached);
      return;
    }

    if (
      isBusy ||
      isSelectingConversation ||
      !canUseTradeDiscovery ||
      (usage && usage.remaining <= 0)
    ) {
      return;
    }

    setInput("");
    setMetaError("");
    setTradeDiscoveryWizard(createTradeDiscoveryWizardState());
    openAssistant();
  }

  async function startAnalyzeMatchesMode() {
    if (
      !canAnalyzeSelectedMatches ||
      isBusy ||
      isSelectingConversation ||
      (usage && usage.remaining <= 0)
    ) {
      return;
    }

    await startNewConversation();
    setIsAnalyzeMatchesMode(true);
    setMetaError("");
    openAssistant();
  }

  function updateTradeDiscoveryWizard(
    updater: (current: TradeDiscoveryWizardState) => TradeDiscoveryWizardState,
  ) {
    setTradeDiscoveryWizard((current) =>
      current ? updater(current) : current,
    );
  }

  function cancelTradeDiscoveryWizard() {
    setTradeDiscoveryWizard(null);

    if (messages.length === 0 && !currentConversationId) {
      setCurrentView("home");
    }
  }

  function backTradeDiscoveryWizard() {
    setTradeDiscoveryWizard((current) => {
      if (!current) {
        return current;
      }

      if (current.eventState === "pre_match" && current.strategy) {
        return {
          ...current,
          strategy: null,
          timeWindowHours: "",
          liveWindowMinutes: "",
        };
      }

      if (current.strategy) {
        return {
          ...current,
          strategy: null,
          timeWindowHours: "",
          liveWindowMinutes: "",
        };
      }

      if (current.sport) {
        return {
          ...current,
          sport: null,
          strategy: null,
          timeWindowHours: "",
          liveWindowMinutes: "",
        };
      }

      if (current.eventState) {
        return createTradeDiscoveryWizardState();
      }

      return current;
    });
  }

  async function submitTradeDiscoveryWizard(
    wizardState: TradeDiscoveryWizardState,
  ) {
    if (
      !wizardState.eventState ||
      !wizardState.sport ||
      !wizardState.strategy
    ) {
      return;
    }

    const timeWindowHours =
      wizardState.eventState === "pre_match"
        ? parseTradeDiscoveryTimeWindow(wizardState.timeWindowHours)
        : null;
    const liveWindowMinutes =
      wizardState.eventState === "live"
        ? parseTradeDiscoveryLiveWindow(wizardState.liveWindowMinutes)
        : null;

    if (
      (wizardState.eventState === "pre_match" && !timeWindowHours) ||
      (wizardState.eventState === "live" && !liveWindowMinutes)
    ) {
      return;
    }

    const strategyLabel = getAssistantTradeStrategyLabel(wizardState.strategy);
    const prompt = buildTradeDiscoveryPrompt({
      locale,
      eventState: wizardState.eventState,
      sport: wizardState.sport,
      strategyLabel,
      timeWindowHours,
      liveWindowMinutes,
    });

    await submitMessage(prompt, {
      requestMode: "trade_discovery",
      tradeDiscoveryRequest: {
        sport: wizardState.sport,
        eventState: wizardState.eventState,
        strategy: wizardState.strategy,
        ...(wizardState.eventState === "pre_match"
          ? { timeWindowHours: timeWindowHours ?? undefined }
          : { liveWindowMinutes: liveWindowMinutes ?? undefined }),
      },
    });
  }

  function handleStarterSuggestionClick(suggestion: string) {
    if (suggestion === text.analyzeMatchesStarter) {
      void startAnalyzeMatchesMode();
      return;
    }

    if (suggestion === text.discoverTradesStarter) {
      startTradeDiscoveryWizard();
      return;
    }

    void submitMessage(suggestion);
  }

  const discoverySportOptions =
    availableTradeDiscoverySports.length > 0
      ? availableTradeDiscoverySports
      : DISCOVERY_SPORT_OPTIONS.map((option) => option.value);

  const discoveryStep = !tradeDiscoveryWizard?.eventState
    ? "event_state"
    : !tradeDiscoveryWizard.sport
      ? "sport"
      : !tradeDiscoveryWizard.strategy
        ? "strategy"
        : tradeDiscoveryWizard.eventState === "pre_match"
          ? !parseTradeDiscoveryTimeWindow(tradeDiscoveryWizard.timeWindowHours)
            ? "time_window"
            : "ready"
          : !parseTradeDiscoveryLiveWindow(
                tradeDiscoveryWizard.liveWindowMinutes,
              )
            ? "live_window"
            : "ready";

  const discoveryStepTitle =
    discoveryStep === "event_state"
      ? text.discoverTradesChooseState
      : discoveryStep === "sport"
        ? text.discoverTradesChooseSport
        : discoveryStep === "strategy"
          ? text.discoverTradesChooseStrategy
          : discoveryStep === "live_window"
            ? text.discoverTradesChooseTimeLive
            : text.discoverTradesChooseTimePreMatch;

  const tradeDiscoveryWizardContent = tradeDiscoveryWizard ? (
    <div className="oddsfantasy-chat-discovery-wizard">
      <div className="oddsfantasy-chat-discovery-wizard-header">
        <div>
          <p className="oddsfantasy-chat-discovery-wizard-eyebrow">
            {text.discoverTradesMenuTitle}
          </p>
          <p className="oddsfantasy-chat-discovery-wizard-title">
            {discoveryStepTitle}
          </p>
        </div>

        <button
          type="button"
          onClick={cancelTradeDiscoveryWizard}
          className="oddsfantasy-chat-discovery-wizard-cancel"
        >
          {text.discoverTradesCancel}
        </button>
      </div>

      <div className="oddsfantasy-chat-discovery-wizard-summary">
        {tradeDiscoveryWizard.eventState ? (
          <button
            type="button"
            onClick={() => {
              updateTradeDiscoveryWizard((current) => ({
                ...current,
                eventState: null,
                sport: null,
                strategy: null,
                timeWindowHours: "",
                liveWindowMinutes: "",
              }));
            }}
            className="oddsfantasy-chat-discovery-wizard-chip"
          >
            {tradeDiscoveryWizard.eventState === "pre_match"
              ? text.discoverTradesPreMatch
              : text.discoverTradesLive}
          </button>
        ) : null}

        {tradeDiscoveryWizard.sport ? (
          <button
            type="button"
            onClick={() => {
              updateTradeDiscoveryWizard((current) => ({
                ...current,
                sport: null,
                strategy: null,
                timeWindowHours: "",
                liveWindowMinutes: "",
              }));
            }}
            className="oddsfantasy-chat-discovery-wizard-chip"
          >
            {getDiscoverySportLabel(tradeDiscoveryWizard.sport, locale)}
          </button>
        ) : null}

        {tradeDiscoveryWizard.strategy ? (
          <button
            type="button"
            onClick={() => {
              updateTradeDiscoveryWizard((current) => ({
                ...current,
                strategy: null,
                timeWindowHours: "",
                liveWindowMinutes: "",
              }));
            }}
            className="oddsfantasy-chat-discovery-wizard-chip"
          >
            {getAssistantTradeStrategyLabel(tradeDiscoveryWizard.strategy)}
          </button>
        ) : null}
      </div>

      {discoveryStep === "event_state" && (
        <div className="oddsfantasy-chat-discovery-wizard-grid oddsfantasy-chat-discovery-wizard-grid-two">
          <button
            type="button"
            onClick={() => {
              updateTradeDiscoveryWizard((current) => ({
                ...current,
                eventState: "pre_match",
                sport: null,
                strategy: null,
                timeWindowHours: "",
                liveWindowMinutes: "",
              }));
            }}
            className="oddsfantasy-chat-discovery-wizard-option"
          >
            {text.discoverTradesPreMatch}
          </button>

          <button
            type="button"
            onClick={() => {
              updateTradeDiscoveryWizard((current) => ({
                ...current,
                eventState: "live",
                sport: null,
                strategy: null,
                timeWindowHours: "",
                liveWindowMinutes: "",
              }));
            }}
            className="oddsfantasy-chat-discovery-wizard-option"
          >
            {text.discoverTradesLive}
          </button>
        </div>
      )}

      {discoveryStep === "sport" && (
        <div className="oddsfantasy-chat-discovery-wizard-grid oddsfantasy-chat-discovery-wizard-grid-two">
          {discoverySportOptions.map((sport) => (
            <button
              key={sport}
              type="button"
              onClick={() => {
                updateTradeDiscoveryWizard((current) => ({
                  ...current,
                  sport,
                  strategy: null,
                  timeWindowHours: "",
                  liveWindowMinutes: "",
                }));
              }}
              className="oddsfantasy-chat-discovery-wizard-option"
            >
              {getDiscoverySportLabel(sport, locale)}
            </button>
          ))}
        </div>
      )}

      {discoveryStep === "strategy" && (
        <div className="oddsfantasy-chat-discovery-wizard-grid oddsfantasy-chat-discovery-wizard-grid-two">
          {tradeDiscoveryStrategies.map((strategy) => (
            <button
              key={strategy.key}
              type="button"
              onClick={() => {
                const nextWizardState = tradeDiscoveryWizard
                  ? {
                      ...tradeDiscoveryWizard,
                      strategy: strategy.key,
                      timeWindowHours: "",
                      liveWindowMinutes: "",
                    }
                  : null;

                if (!nextWizardState) {
                  return;
                }

                setTradeDiscoveryWizard(nextWizardState);
              }}
              className="oddsfantasy-chat-discovery-wizard-option"
            >
              {strategy.label}
            </button>
          ))}
        </div>
      )}

      {tradeDiscoveryWizard.eventState === "pre_match" &&
        (discoveryStep === "time_window" || discoveryStep === "ready") && (
          <div className="space-y-3">
            <div className="oddsfantasy-chat-discovery-wizard-grid oddsfantasy-chat-discovery-wizard-grid-four">
              {DISCOVERY_TIME_WINDOW_PRESETS.map((hours) => (
                <button
                  key={hours}
                  type="button"
                  onClick={() => {
                    const nextWizardState = tradeDiscoveryWizard
                      ? {
                          ...tradeDiscoveryWizard,
                          timeWindowHours: String(hours),
                        }
                      : null;

                    if (!nextWizardState) {
                      return;
                    }

                    setTradeDiscoveryWizard(nextWizardState);
                    void submitTradeDiscoveryWizard(nextWizardState);
                  }}
                  className="oddsfantasy-chat-discovery-wizard-option oddsfantasy-chat-discovery-wizard-option-centered"
                >
                  {hours}h
                </button>
              ))}
            </div>

            <div className="oddsfantasy-chat-discovery-wizard-time-row">
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={tradeDiscoveryWizard.timeWindowHours}
                onChange={(event) => {
                  updateTradeDiscoveryWizard((current) => ({
                    ...current,
                    timeWindowHours: event.target.value,
                  }));
                }}
                placeholder={text.discoverTradesTimePlaceholder}
                className="oddsfantasy-chat-discovery-wizard-input"
              />

              <button
                type="button"
                onClick={() => {
                  void submitTradeDiscoveryWizard(tradeDiscoveryWizard);
                }}
                disabled={
                  !parseTradeDiscoveryTimeWindow(
                    tradeDiscoveryWizard.timeWindowHours,
                  )
                }
                className="oddsfantasy-chat-discovery-wizard-submit"
              >
                {text.discoverTradesSearch}
              </button>
            </div>
          </div>
        )}

      {tradeDiscoveryWizard.eventState === "live" &&
        (discoveryStep === "live_window" || discoveryStep === "ready") && (
          <div className="space-y-3">
            <div className="oddsfantasy-chat-discovery-wizard-grid oddsfantasy-chat-discovery-wizard-grid-four">
              {DISCOVERY_LIVE_WINDOW_PRESETS.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => {
                    const nextWizardState = tradeDiscoveryWizard
                      ? {
                          ...tradeDiscoveryWizard,
                          liveWindowMinutes: String(minutes),
                        }
                      : null;

                    if (!nextWizardState) {
                      return;
                    }

                    setTradeDiscoveryWizard(nextWizardState);
                    void submitTradeDiscoveryWizard(nextWizardState);
                  }}
                  className="oddsfantasy-chat-discovery-wizard-option oddsfantasy-chat-discovery-wizard-option-centered"
                >
                  {minutes}m
                </button>
              ))}
            </div>

            <div className="oddsfantasy-chat-discovery-wizard-time-row">
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={tradeDiscoveryWizard.liveWindowMinutes}
                onChange={(event) => {
                  updateTradeDiscoveryWizard((current) => ({
                    ...current,
                    liveWindowMinutes: event.target.value,
                  }));
                }}
                placeholder={text.discoverTradesLiveTimePlaceholder}
                className="oddsfantasy-chat-discovery-wizard-input"
              />

              <button
                type="button"
                onClick={() => {
                  void submitTradeDiscoveryWizard(tradeDiscoveryWizard);
                }}
                disabled={
                  !parseTradeDiscoveryLiveWindow(
                    tradeDiscoveryWizard.liveWindowMinutes,
                  )
                }
                className="oddsfantasy-chat-discovery-wizard-submit"
              >
                {text.discoverTradesSearch}
              </button>
            </div>
          </div>
        )}

      {(tradeDiscoveryWizard.eventState ||
        tradeDiscoveryWizard.sport ||
        tradeDiscoveryWizard.strategy) && (
        <div className="oddsfantasy-chat-discovery-wizard-actions">
          <button
            type="button"
            onClick={backTradeDiscoveryWizard}
            className="oddsfantasy-chat-discovery-wizard-secondary"
          >
            {text.discoverTradesBack}
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div
      className={clsx(
        "oddsfantasy-chat-shell",
        isTradePage && "oddsfantasy-chat-shell-trade-mobile-offset",
      )}
    >
      {isOpen && (
        <section className="oddsfantasy-chat-panel">
          <header className="oddsfantasy-chat-header">
            <div className="flex items-center gap-3">
              {currentView === "conversation" ? (
                <button
                  type="button"
                  onClick={() => {
                    setTradeDiscoveryWizard(null);
                    setCurrentView("home");
                  }}
                  className="oddsfantasy-chat-header-avatar"
                  aria-label={text.home}
                >
                  <ChevronLeft size={18} />
                </button>
              ) : (
                <div className="oddsfantasy-chat-header-avatar">
                  <Bot size={18} />
                </div>
              )}
              <div>
                <p className="oddsfantasy-chat-header-title">{text.title}</p>
                <p className="oddsfantasy-chat-header-subtitle">
                  {text.subtitle}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={closeAssistant}
                className="oddsfantasy-chat-icon-button"
                aria-label={text.close}
              >
                <X size={16} />
              </button>
            </div>
          </header>

          {isSelectingConversation && (
            <div
              className="oddsfantasy-chat-header-progress"
              aria-hidden="true"
            >
              <div className="oddsfantasy-chat-header-progress-bar" />
            </div>
          )}

          <div
            ref={bodyRef}
            onScroll={handleBodyScroll}
            className="oddsfantasy-chat-body oddsfantasy-chat-scrollbar"
          >
            {currentView === "home" ? (
              <div className="space-y-4">
                <div className="oddsfantasy-chat-empty">
                  <p className="oddsfantasy-chat-empty-title">
                    {text.homeTitle}
                  </p>
                  <p className="oddsfantasy-chat-empty-welcome">
                    {text.welcome}
                  </p>
                  <p className="oddsfantasy-chat-empty-body">{text.homeBody}</p>
                  {usage && (
                    <div className="oddsfantasy-chat-meta-card">
                      <p>
                        <strong>{getPlanLabel(plan)} plan</strong>
                      </p>
                      <p className="mt-1">
                        {text.usage}: {usage.remaining}/{usage.limit}
                      </p>
                      {plan === "free" &&
                      tradeDiscoveryUsage &&
                      canUseTradeDiscovery ? (
                        <p className="mt-1">
                          {text.discoveryUsage}: {tradeDiscoveryUsage.remaining}
                          /{tradeDiscoveryUsage.limit}
                        </p>
                      ) : null}
                      <p className="mt-1">
                        {text.tools}:{" "}
                        {enabledTools.map((tool) => tool.label).join(", ") ||
                          "None"}
                      </p>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    void startNewConversation();
                  }}
                  disabled={isBusy || isSelectingConversation}
                  className="oddsfantasy-chat-suggestion"
                >
                  {text.newConversation}
                </button>

                <div className="grid gap-2">
                  {starterSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      disabled={isBusy || isSelectingConversation}
                      onClick={() => {
                        handleStarterSuggestionClick(suggestion);
                      }}
                      className="oddsfantasy-chat-suggestion"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>

                {tradeDiscoveryWizardContent}

                <div className="space-y-2">
                  <p className="oddsfantasy-chat-empty-title text-sm">
                    {text.recentConversations}
                  </p>

                  {conversations.length === 0 ? (
                    <p className="oddsfantasy-chat-empty-body">
                      {text.noConversations}
                    </p>
                  ) : (
                    <div className="grid gap-2">
                      {conversations.map((conversation) => (
                        <div
                          key={conversation.conversationId}
                          className={clsx(
                            "oddsfantasy-chat-conversation-card",
                            conversation.conversationId ===
                              currentConversationId &&
                              "ring-2 ring-[#3f4bf4]/30",
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              void openConversation(
                                conversation.conversationId,
                              );
                            }}
                            disabled={
                              isBusy ||
                              isSelectingConversation ||
                              deletingConversationId ===
                                conversation.conversationId
                            }
                            className="oddsfantasy-chat-conversation-open"
                          >
                            <span className="block max-w-full truncate font-semibold text-slate-900">
                              {conversation.title}
                            </span>
                            <span className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                              <Clock3 size={12} />
                              {formatConversationTimestamp(
                                conversation.updatedAt,
                              )}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              void deleteConversation(
                                conversation.conversationId,
                              );
                            }}
                            disabled={
                              isBusy ||
                              isSelectingConversation ||
                              deletingConversationId !== null
                            }
                            className="oddsfantasy-chat-conversation-delete"
                            aria-label={text.deleteConversation}
                            title={text.deleteConversation}
                          >
                            {deletingConversationId ===
                            conversation.conversationId ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {isLoadingMoreConversations && (
                    <div className="oddsfantasy-chat-conversation-loading">
                      <Loader2 size={14} className="animate-spin" />
                      <span>{text.loadingConversations}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : tradeDiscoveryWizard ? (
              <div className="space-y-4">
                {tradeDiscoveryWizardContent}

                {messages.length > 0 && (
                  <div className="space-y-3">
                    {messages.map((message) => {
                      const content = getMessageText(message);
                      const tradeDiscoveryResults = getTradeDiscoveryResults(
                        message.parts,
                      );
                      const isAssistant = message.role === "assistant";
                      const showTradeDiscoveryCards =
                        isAssistant && tradeDiscoveryResults.length > 0;

                      if (!content && !showTradeDiscoveryCards) {
                        return null;
                      }

                      return (
                        <article
                          key={message.id}
                          className={clsx(
                            "oddsfantasy-chat-message-row",
                            isAssistant
                              ? "oddsfantasy-chat-message-row-assistant"
                              : "oddsfantasy-chat-message-row-user",
                          )}
                        >
                          <div
                            className={clsx(
                              "oddsfantasy-chat-message",
                              isAssistant
                                ? "oddsfantasy-chat-message-assistant"
                                : "oddsfantasy-chat-message-user",
                            )}
                          >
                            {isAssistant && (
                              <div className="oddsfantasy-chat-message-heading">
                                <span className="oddsfantasy-chat-message-heading-icon">
                                  <Bot size={12} />
                                </span>
                                <p
                                  className={clsx(
                                    "oddsfantasy-chat-message-label",
                                    "oddsfantasy-chat-message-label-assistant",
                                  )}
                                >
                                  {text.title}
                                </p>
                              </div>
                            )}
                            {showTradeDiscoveryCards ? (
                              <div className="space-y-3">
                                {renderTradeDiscoveryResults({
                                  results: tradeDiscoveryResults,
                                  locale,
                                  labels: text,
                                })}
                                {content
                                  ? renderAssistantMarkdown(content)
                                  : null}
                              </div>
                            ) : isAssistant ? (
                              renderAssistantMarkdown(content)
                            ) : (
                              <p className="whitespace-pre-wrap text-sm leading-6 text-white">
                                {content}
                              </p>
                            )}
                          </div>

                          {!isAssistant && (
                            <div className="oddsfantasy-chat-message-avatar oddsfantasy-chat-message-avatar-user">
                              <User2 size={14} />
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : messages.length === 0 ? (
              <div className="space-y-4">
                <div className="oddsfantasy-chat-empty">
                  <p className="oddsfantasy-chat-empty-title">
                    {text.emptyTitle}
                  </p>
                  <p className="oddsfantasy-chat-empty-welcome">
                    {text.welcome}
                  </p>
                  <p className="oddsfantasy-chat-empty-body">
                    {text.emptyBody}
                  </p>
                </div>

                <div className="grid gap-2">
                  {starterSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      disabled={isBusy || isSelectingConversation}
                      onClick={() => {
                        handleStarterSuggestionClick(suggestion);
                      }}
                      className="oddsfantasy-chat-suggestion"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message) => {
                  const content = getMessageText(message);
                  const tradeDiscoveryResults = getTradeDiscoveryResults(
                    message.parts,
                  );
                  const isAssistant = message.role === "assistant";
                  const showTradeDiscoveryCards =
                    isAssistant && tradeDiscoveryResults.length > 0;

                  if (!content && !showTradeDiscoveryCards) {
                    return null;
                  }

                  return (
                    <article
                      key={message.id}
                      className={clsx(
                        "oddsfantasy-chat-message-row",
                        isAssistant
                          ? "oddsfantasy-chat-message-row-assistant"
                          : "oddsfantasy-chat-message-row-user",
                      )}
                    >
                      <div
                        className={clsx(
                          "oddsfantasy-chat-message",
                          isAssistant
                            ? "oddsfantasy-chat-message-assistant"
                            : "oddsfantasy-chat-message-user",
                        )}
                      >
                        {isAssistant && (
                          <div className="oddsfantasy-chat-message-heading">
                            <span className="oddsfantasy-chat-message-heading-icon">
                              <Bot size={12} />
                            </span>
                            <p
                              className={clsx(
                                "oddsfantasy-chat-message-label",
                                "oddsfantasy-chat-message-label-assistant",
                              )}
                            >
                              {text.title}
                            </p>
                          </div>
                        )}
                        {showTradeDiscoveryCards ? (
                          <div className="space-y-3">
                            {renderTradeDiscoveryResults({
                              results: tradeDiscoveryResults,
                              locale,
                              labels: text,
                            })}
                            {content ? renderAssistantMarkdown(content) : null}
                          </div>
                        ) : isAssistant ? (
                          renderAssistantMarkdown(content)
                        ) : (
                          <p className="whitespace-pre-wrap text-sm leading-6 text-white">
                            {content}
                          </p>
                        )}
                      </div>

                      {!isAssistant && (
                        <div className="oddsfantasy-chat-message-avatar oddsfantasy-chat-message-avatar-user">
                          <User2 size={14} />
                        </div>
                      )}
                    </article>
                  );
                })}

                {showAssistantTypingRow && (
                  <article className="oddsfantasy-chat-message-row oddsfantasy-chat-message-row-assistant">
                    <div className="oddsfantasy-chat-message oddsfantasy-chat-message-assistant">
                      <div className="oddsfantasy-chat-message-heading">
                        <span className="oddsfantasy-chat-message-heading-icon">
                          <Bot size={12} />
                        </span>
                        <p
                          className={clsx(
                            "oddsfantasy-chat-message-label",
                            "oddsfantasy-chat-message-label-assistant",
                          )}
                        >
                          {text.title}
                        </p>
                      </div>

                      <div
                        className="oddsfantasy-chat-typing"
                        aria-label={text.thinking}
                        aria-live="polite"
                      >
                        <span className="oddsfantasy-chat-typing-dot" />
                        <span className="oddsfantasy-chat-typing-dot" />
                        <span className="oddsfantasy-chat-typing-dot" />
                      </div>
                    </div>
                  </article>
                )}
              </div>
            )}
          </div>

          <div className="oddsfantasy-chat-footer">
            {canAnalyzeSelectedMatches && selectedMatchContext && (
              <div className="mb-3 rounded-[20px] border border-indigo-200 bg-[linear-gradient(135deg,#eef2ff_0%,#f5f3ff_100%)] p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3f4bf4]">
                      {text.selectedMatches}
                    </p>
                    <p className="mt-0.5 truncate text-sm font-semibold text-slate-900">
                      {selectedMatchContext.matches.length}{" "}
                      {selectedMatchContext.matches.length === 1
                        ? text.selectedMatchCount
                        : text.selectedMatchesCount}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (isAnalyzeMatchesMode) {
                        setIsAnalyzeMatchesMode(false);
                        return;
                      }

                      void startAnalyzeMatchesMode();
                    }}
                    disabled={isBusy || isSelectingConversation}
                    className={clsx(
                      "shrink-0 rounded-xl px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                      isAnalyzeMatchesMode
                        ? "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        : "bg-[#3f4bf4] text-white hover:bg-[#303bd8]",
                    )}
                  >
                    {isAnalyzeMatchesMode
                      ? text.exitAnalyzeMatches
                      : text.analyzeMatchesStarter}
                  </button>
                </div>

                {isAnalyzeMatchesMode && (
                  <p className="mt-2 text-xs leading-5 text-slate-600">
                    {text.analyzeMatchesBody}
                  </p>
                )}
              </div>
            )}

            {canUseTradeAnalysis && activeTradeContext && (
              <div className="mb-3 rounded-[20px] border border-[#3f4bf4]/15 bg-[#eef2ff] p-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex flex-1 items-center gap-2">
                    <p className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3f4bf4]">
                      {text.selectedTrade}
                    </p>
                    <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                      {formatTradeSelectionTitle(activeTradeContext)}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setIsSelectedTradeCardMinimized((value) => !value);
                    }}
                    className="shrink-0 text-xs font-medium text-slate-500 transition-colors hover:text-slate-800"
                  >
                    {isSelectedTradeCardMinimized
                      ? text.expandSelection
                      : text.minimizeSelection}
                  </button>
                </div>

                {!isSelectedTradeCardMinimized && canAnalyzeSelectedTrade && (
                  <>
                    <p className="mt-2 text-xs font-medium text-slate-600">
                      {text.chooseStrategy}
                    </p>

                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {visibleTradeStrategies.map((strategy) => {
                        const button = (
                          <button
                            key={strategy.key}
                            type="button"
                            onClick={() => {
                              if (
                                plan === "free" &&
                                strategy.key === "build_with_ai"
                              ) {
                                setTradeStrategyNotice(
                                  text.buildWithAiPremiumOnly,
                                );
                                return;
                              }

                              setTradeStrategyNotice("");
                              const nextTradeContext =
                                getTradeContextForStrategy(
                                  strategy.key,
                                  activeTradeContext,
                                );

                              if (nextTradeContext) {
                                setAssistantTradeContext(nextTradeContext);
                                setSelectedTradeContext(nextTradeContext);
                              }

                              void submitMessage(
                                getStrategySubmitPrompt(strategy.key),
                                {
                                  requestMode:
                                    strategy.key === "build_with_ai"
                                      ? "build_with_ai"
                                      : "trade_analysis",
                                  selectedTradeContextOverride:
                                    nextTradeContext,
                                },
                              );
                            }}
                            disabled={isBusy || isSelectingConversation}
                            className={clsx(
                              "px-3 py-2 text-left text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                              strategy.key === "build_with_ai"
                                ? "w-[calc(50%-0.25rem)] rounded-2xl border border-emerald-300 bg-emerald-500 text-center text-white hover:border-emerald-400 hover:bg-emerald-600"
                                : "rounded-2xl border border-[#3f4bf4]/14 bg-white text-slate-700 hover:border-[#3f4bf4]/35 hover:bg-[#f8f9ff]",
                            )}
                          >
                            {strategy.label}
                          </button>
                        );

                        if (strategy.key === "build_with_ai") {
                          return (
                            <div
                              key={strategy.key}
                              className="col-span-2 flex justify-center"
                            >
                              {button}
                            </div>
                          );
                        }

                        return button;
                      })}
                    </div>

                    {tradeStrategyNotice && (
                      <p className="oddsfantasy-chat-alert oddsfantasy-chat-alert-warning mt-3">
                        {tradeStrategyNotice}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {(error || metaError) && (
              <p className="oddsfantasy-chat-alert oddsfantasy-chat-alert-error">
                {metaError ||
                  getChatErrorMessage(
                    error,
                    text.error,
                    text.assistantNotConfigured,
                  )}
              </p>
            )}

            {usage && (
              <p className="oddsfantasy-chat-footer-meta">
                {getPlanLabel(plan)}. {text.usage}: {usage.remaining}/
                {usage.limit}
                {plan === "free" && tradeDiscoveryUsage && canUseTradeDiscovery
                  ? ` • ${text.discoveryUsage}: ${tradeDiscoveryUsage.remaining}/${tradeDiscoveryUsage.limit}`
                  : ""}
              </p>
            )}

            {usage && usage.remaining <= 0 && (
              <p className="oddsfantasy-chat-alert oddsfantasy-chat-alert-warning">
                {text.dailyLimitReached}
              </p>
            )}

            {isBusy && (
              <div className="oddsfantasy-chat-footer-status">
                <span>{text.thinking}</span>

                <button
                  type="button"
                  onClick={() => stop()}
                  className="oddsfantasy-chat-stop-button"
                >
                  <Square size={10} />
                  {text.stop}
                </button>
              </div>
            )}

            {!tradeDiscoveryWizard && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitMessage(input);
                }}
                className="oddsfantasy-chat-composer"
              >
                <textarea
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void submitMessage(input);
                    }
                  }}
                  rows={1}
                  placeholder={
                    isAnalyzeMatchesMode
                      ? text.analyzeMatchesPlaceholder
                      : text.placeholder
                  }
                  disabled={usage ? usage.remaining <= 0 : false}
                  className="oddsfantasy-chat-input"
                />

                <button
                  type="submit"
                  disabled={
                    !input.trim() ||
                    isBusy ||
                    (usage ? usage.remaining <= 0 : false)
                  }
                  className="oddsfantasy-chat-send-button"
                  aria-label="Send message"
                >
                  {isBusy ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <SendHorizonal size={18} />
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      )}

      {showReminder && isHomePage && !isOpen && !isTradePage && (
        <button
          type="button"
          onClick={() => {
            openAssistant();
          }}
          className="oddsfantasy-chat-reminder"
          aria-label={text.reminderTitle}
        >
          <div className="oddsfantasy-chat-reminder-dot" />
          <div>
            <p className="oddsfantasy-chat-reminder-title">
              {text.reminderTitle}
            </p>
            <p className="oddsfantasy-chat-reminder-body">
              {text.reminderBody}
            </p>
          </div>
        </button>
      )}

      {!isTradePage && (
        <button
          type="button"
          onClick={() => {
            if (isOpen) {
              closeAssistant();
              return;
            }

            openAssistant();
          }}
          className={clsx(
            "oddsfantasy-chat-launcher",
            isOpen && "oddsfantasy-chat-launcher-active",
          )}
          aria-label="Toggle OddsFantasy assistant"
        >
          <span className="oddsfantasy-chat-launcher-ring" />
          <span className="oddsfantasy-chat-launcher-inner">
            <MessageSquare size={24} />
          </span>
        </button>
      )}
    </div>
  );
}
